import React from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, ArrowRight, TrendingUp } from 'lucide-react';
import type { SubmarketScore } from '../../types/calculations';
import type { FullComparisonResponse } from '../../types/yardi';
import { SectionTitle } from '../Luxury';
import { useActiveMarket } from '../../store/useMarketStore';

interface ZandiYardiCrossValidationProps {
    zandi: SubmarketScore;
    yardiData: FullComparisonResponse;
}

type DivergenceLevel = 'agreement' | 'moderate' | 'strong';

function generateNarrative(
    zandiPct: number,
    yardiPct: number,
    divergenceLevel: DivergenceLevel,
    zandiHigher: boolean,
    submarketName: string,
    metroName: string,
): string {
    const zandiStr = `${zandiPct.toFixed(1)}%`;
    const yardiStr = `${yardiPct.toFixed(1)}%`;

    // Check negative growth scenarios first
    const bothNegative = zandiPct < 0 && yardiPct < 0;
    const zandiPositiveYardiNegative = zandiPct >= 0 && yardiPct < 0;
    const zandiNegativeYardiPositive = zandiPct < 0 && yardiPct >= 0;

    if (bothNegative) {
        return `Both sources signal contraction. Zandi shows ${zandiStr} 5-year EGI decline for ${submarketName} and Yardi shows ${yardiStr} ${metroName} metro YoY rent decline. This market is under stress — proceed with extreme caution and stress-test downside scenarios.`;
    }
    if (zandiPositiveYardiNegative) {
        return `Fundamental divergence: Zandi shows positive ${zandiStr} 5-year EGI growth for ${submarketName} but Yardi reports ${yardiStr} metro rent decline. The submarket's longer-term trend is healthy but the metro is experiencing a near-term pullback — monitor closely.`;
    }
    if (zandiNegativeYardiPositive) {
        return `Fundamental divergence: This submarket's 5-year EGI trend is declining (${zandiStr}) despite ${yardiStr} metro-level rent growth. The broader ${metroName} metro is recovering but this submarket has structural headwinds — investigate local factors before investing.`;
    }

    // Standard divergence classification
    if (divergenceLevel === 'agreement') {
        const assessment = zandiPct >= 3 ? 'positive' : zandiPct >= 1 ? 'moderate' : 'weak';
        return `Both Zandi submarket data and Yardi metro benchmarks show ${assessment} growth. Zandi's 5-year EGI CAGR of ${zandiStr} for ${submarketName} and Yardi's ${yardiStr} ${metroName} metro YoY rent growth both signal a healthy trend.`;
    }
    if (divergenceLevel === 'moderate' && zandiHigher) {
        return `Zandi shows ${zandiStr} 5-year EGI CAGR for ${submarketName} while Yardi metro rent growth is ${yardiStr} YoY — this submarket appears to be outperforming the broader ${metroName} metro. The difference may reflect submarket-specific value-add activity or the 5-year vs 1-year timeframe difference.`;
    }
    if (divergenceLevel === 'moderate' && !zandiHigher) {
        return `Yardi shows ${yardiStr} metro-level rent growth but this submarket's Zandi EGI CAGR is only ${zandiStr} over 5 years. The submarket may be lagging the ${metroName} metro average — investigate local headwinds.`;
    }
    if (divergenceLevel === 'strong' && zandiHigher) {
        return `Significant divergence: Zandi shows ${zandiStr} EGI CAGR for ${submarketName} vs Yardi's ${yardiStr} ${metroName} metro YoY. This submarket is dramatically outperforming the metro — strong signal for value-add investors, but verify the trend is sustainable.`;
    }
    // strong, Yardi higher
    return `Significant divergence: Yardi ${metroName} metro rent growth (${yardiStr}) exceeds this submarket's Zandi EGI CAGR (${zandiStr}). This submarket is underperforming — build extra margin of safety into your underwriting.`;
}

export const ZandiYardiCrossValidation: React.FC<ZandiYardiCrossValidationProps> = ({ zandi, yardiData }) => {
    const market = useActiveMarket();
    // Guard: need Zandi growth data and Yardi rent growth
    const rawZandiGrowth = zandi.raw_data.egi_cagr_5yr ?? zandi.raw_data.cagr_5yr ?? null;
    if (rawZandiGrowth === null || !yardiData?.rent_growth_comparison) return null;

    // Defensive: if value > 1 it's a percentage (e.g. 7.7), divide by 100
    const zandiGrowthDecimal = rawZandiGrowth > 1 ? rawZandiGrowth / 100 : rawZandiGrowth;
    const yardiGrowth = yardiData.rent_growth_comparison.yardi_value;

    const divergenceBp = Math.abs(zandiGrowthDecimal - yardiGrowth) * 10000;
    const zandiHigher = zandiGrowthDecimal > yardiGrowth;

    const divergenceLevel: DivergenceLevel =
        divergenceBp > 300 ? 'strong' :
        divergenceBp > 150 ? 'moderate' :
        'agreement';

    const metroName = yardiData.metro_name ?? market.display_name;
    const submarketName = zandi.name;

    const zandiPct = zandiGrowthDecimal * 100;
    const yardiPct = yardiGrowth * 100;

    const narrative = generateNarrative(zandiPct, yardiPct, divergenceLevel, zandiHigher, submarketName, metroName);

    // Divergence callout styling
    const calloutStyles = {
        agreement: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: CheckCircle, iconColor: 'text-emerald-400', label: 'Sources Aligned' },
        moderate: { bg: 'bg-amber-400/10', border: 'border-amber-400/30', icon: AlertCircle, iconColor: 'text-amber-400', label: 'Moderate Divergence' },
        strong: { bg: 'bg-red-400/10', border: 'border-red-400/30', icon: AlertTriangle, iconColor: 'text-red-400', label: 'Strong Divergence' },
    };
    const callout = calloutStyles[divergenceLevel];
    const CalloutIcon = callout.icon;

    // Secondary metrics
    const vacancyRate = zandi.raw_data.vacancy_rate;
    const momentumScore = zandi.momentum_score;
    const yardiForecast = yardiData.market_context.forecast_2026;
    const permitDensity = zandi.raw_data.permit_density;
    const completionsPct = yardiData.market_context.completions_pct;

    return (
        <div className="bg-charcoal rounded-xl p-6 border border-purple-500/30">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    <SectionTitle>Zandi × Yardi Cross-Validation</SectionTitle>
                </div>
                <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-sm font-semibold uppercase tracking-wider bg-purple-500/20 text-purple-300 rounded">Zandi</span>
                    <span className="px-2 py-0.5 text-sm font-semibold uppercase tracking-wider bg-amber-500/20 text-amber-400 rounded">Yardi</span>
                </div>
            </div>

            {/* Primary Comparison Row */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center mb-5">
                {/* Zandi Box */}
                <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20 text-center">
                    <p className="text-sm text-purple-300 uppercase tracking-wider mb-1">Zandi Submarket</p>
                    <p className={`text-2xl font-bold font-mono ${zandiGrowthDecimal >= 0 ? 'text-purple-300' : 'text-red-400'}`}>
                        {zandiPct.toFixed(1)}%
                    </p>
                    <p className="text-sm text-stone-700 mt-1">5-Year CAGR</p>
                    <p className="text-sm text-purple-400/70 mt-0.5">{submarketName}</p>
                </div>

                {/* Center Divergence Indicator */}
                <div className="flex flex-col items-center gap-1">
                    <ArrowRight className={`w-5 h-5 ${callout.iconColor}`} />
                    <span className={`text-sm font-mono font-semibold ${callout.iconColor}`}>
                        {divergenceBp.toFixed(0)}bp
                    </span>
                </div>

                {/* Yardi Box */}
                <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/20 text-center">
                    <p className="text-sm text-amber-400 uppercase tracking-wider mb-1">Yardi Metro</p>
                    <p className={`text-2xl font-bold font-mono ${yardiGrowth >= 0 ? 'text-amber-300' : 'text-red-400'}`}>
                        {yardiPct.toFixed(1)}%
                    </p>
                    <p className="text-sm text-stone-700 mt-1">1-Year YoY</p>
                    <p className="text-sm text-amber-400/70 mt-0.5">{metroName}</p>
                </div>
            </div>

            {/* Divergence Callout */}
            <div className={`${callout.bg} border ${callout.border} rounded-lg p-4 mb-5`}>
                <div className="flex items-start gap-3">
                    <CalloutIcon className={`w-5 h-5 ${callout.iconColor} flex-shrink-0 mt-0.5`} />
                    <div>
                        <p className={`text-sm font-semibold ${callout.iconColor} mb-1`}>{callout.label}</p>
                        <p className="text-sm text-stone-700 leading-relaxed">{narrative}</p>
                    </div>
                </div>
            </div>

            {/* Scope callout */}
            <p className="text-sm text-zinc-500 mb-3">
                Yardi metro data used as {metroName}-wide benchmark; submarket-specific Yardi data not available.
            </p>

            {/* Secondary Metrics Grid */}
            <div className="grid grid-cols-3 gap-4 mb-4">
                {/* Vacancy */}
                <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-sm text-zinc-500 mb-1">Vacancy</p>
                    <p className="text-sm text-zinc-200 font-medium font-mono">
                        {vacancyRate != null ? `${(vacancyRate * 100).toFixed(1)}%` : '—'}
                    </p>
                    <p className="text-sm text-zinc-600 mt-0.5">National avg: 5.5%</p>
                </div>

                {/* Momentum */}
                {(momentumScore != null || yardiForecast != null) ? (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                        <p className="text-sm text-zinc-500 mb-1">Momentum</p>
                        <div className="space-y-1">
                            {momentumScore != null && (
                                <p className="text-sm text-zinc-200 font-medium">
                                    <span className="text-purple-300 font-mono">{momentumScore.toFixed(0)}</span>
                                    <span className="text-zinc-500">/100</span>
                                </p>
                            )}
                            {yardiForecast != null && (
                                <p className="text-sm text-amber-400/70 font-mono">
                                    {(yardiForecast * 100).toFixed(1)}% forecast
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                        <p className="text-sm text-zinc-500 mb-1">Momentum</p>
                        <p className="text-sm text-zinc-200 font-medium">—</p>
                    </div>
                )}

                {/* Supply */}
                {(permitDensity != null || completionsPct != null) ? (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                        <p className="text-sm text-zinc-500 mb-1">Supply</p>
                        <div className="space-y-1">
                            {permitDensity != null && (
                                <p className="text-sm text-zinc-200 font-medium font-mono">
                                    {permitDensity.toFixed(1)} <span className="text-zinc-500 text-sm">permits/mi²</span>
                                </p>
                            )}
                            {completionsPct != null && (
                                <p className="text-sm text-amber-400/70 font-mono">
                                    {(completionsPct * 100).toFixed(1)}% completions
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                        <p className="text-sm text-zinc-500 mb-1">Supply</p>
                        <p className="text-sm text-zinc-200 font-medium">—</p>
                    </div>
                )}
            </div>

            {/* Source Footer */}
            <p className="text-sm text-zinc-500">
                Sources: Zandi/Moody's (submarket, 5yr CAGR) · Yardi Matrix ({metroName} metro, Jan 2026 YoY)
            </p>

            {/* Timeframe Disclaimer */}
            <p className="text-sm text-zinc-600 mt-1 italic">
                Note: Zandi measures 5-year compound growth at the submarket level. Yardi measures 1-year rent change at the metro level. Direct numeric comparison should account for these differences.
            </p>
        </div>
    );
};

export default ZandiYardiCrossValidation;
